<template>
  <div
    class="returns-center"
    :class="{
      'padding-top-xxs': isMcom,
      'padding-top-xs': isBcom,
    }"
  >
    <div
      :class="{
        'padding-bottom-xs': isMcom,
        'padding-bottom-l': isBcom,
        'border-bottom small-12': true,
      }"
    >
      <component :is="isBcom ? 'h1' : 'h3'"> Return Center </component>
    </div>
    <div
      :class="{
        'medium-margin-top-xl large-margin-top-xxxl small-margin-top-m': isBcom,
        'margin-top-xl': isMcom,
        'grid-x gap-5 align-center text-center card-container margin-bottom-xs': true,
      }"
    >
      <Card
        v-for="(item, index) in Object.values(returnCenterTextConfig)"
        :key="item.type"
        tabindex="0"
        :padding-class="getPaddingClass"
        class="return-card"
        :class="{
          'large-margin-right-l medium-margin-right-l small-margin-bottom-s medium-margin-bottom-m': isBcom,
          'margin-right-xs small-margin-bottom-xs': isMcom,
        }"
        :aria-labelledby="'return-option-title-' + index + ' ' + 'return-option-text-' + index"
        @click.stop.prevent="handleClick(item.type)"
        @keyup.enter="handleClick(item.type)"
      >
        <div>
          <span :class="item.icon"></span>
          <component :is="isBcom ? 'h3' : 'div'" :id="'return-option-title-' + index" :class="{ 'margin-top-xs': isBcom, 'card-title': isMcom }">
            {{ item.title }}
          </component>
          <div :id="'return-option-text-' + index" :class="{ 'large margin-top-l margin-bottom-l': isBcom }">
            {{ item.text }}
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { UserStatus, Cookie } from '@nuxt-common/util/app/utils/cookie';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import Card from '@nuxt-common/atomic-ui/app/components/card/Card';
import useBrand from '~/composables/useBrand';
import { RETURN_CENTER_TEXT } from 'config';

const envPropsStore = useEnvPropsStore();
const { isMcom, isBcom } = useBrand();
const router = useRouter();

// Reactive state
const isSignedIn = ref(false);

// Computed properties
const returnCenterTextConfig = computed(() => RETURN_CENTER_TEXT);

const getPaddingClass = computed(() => (isBcom ? 'padding-l' : 'padding-s'));

// Methods
const handleClick = (type) => {
  const actionMap = {
    START_RETURN: {
      eventName: 'start return',
      customLinkName: 'return center_start a return',
      url: isSignedIn.value ? '/purchases/online' : RETURN_CENTER_TEXT.startReturn.url,
      externalLink: !isSignedIn.value,
    },
    GIFT_RETURN: {
      eventName: 'gift returns',
      customLinkName: 'return center_gift returns',
      url: isSignedIn.value ? '/returns/gifts' : RETURN_CENTER_TEXT.giftReturn.url,
      externalLink: !isSignedIn.value,
    },
    FAQ_RETURN: {
      eventName: 'return faqs',
      customLinkName: 'return center_return faqs',
      url: RETURN_CENTER_TEXT.returnFaqs.url,
      externalLink: true,
    },
  };

  const { eventName, customLinkName, url, externalLink = false } = actionMap[type] || {};
  fireTag('link', { event_name: eventName, custom_link_name: customLinkName });

  if (!isSignedIn.value && type === 'GIFT_RETURN') {
    const domain = envPropsStore.envProps.cookieDomain;
    Cookie.set('FORWARDPAGE_KEY', `${window.location.origin}/returns/gifts`, null, { domain });
  }

  if (externalLink) window.location.href = url;
  else router.push(url);
};

// Lifecycle hooks
onMounted(() => {
  fireTag('view', {
    page_name: 'return center landing page',
    page_type: 'return center',
  });
  isSignedIn.value = UserStatus.isSignedIn();
});
</script>

<style lang="scss" scoped>
.card-container {
  justify-content: center;
  width: 100%;
}

.return-card {
  display: block;
  cursor: pointer;

  &:hover > div,
  &:focus > div {
    transform: translateY(-0.5rem);
    transition: all 1s;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_order-history.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_registry-box.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_return.scss';

.card {
  border-radius: rem-calc(5px);
  max-width: rem-calc(260);
  border: 2px solid $gray-3-color !important;
}

.card-title {
  font-weight: bold;
  margin-bottom: 1rem;
  font-size: 1rem;
}
</style>

<style lang="scss" brand="bcom">
@import '~@core/csg/src/scss/encodedIcons/bcom/_order-status.scss';
@import '~@core/csg/src/scss/encodedIcons/bcom/_gift-wrap.scss';
@import '~@core/csg/src/scss/encodedIcons/bcom/_return.scss';

.card {
  background-color: $background-color;
  max-width: rem-calc(342);
  border: none !important;
}

.icon {
  width: 48px;
  height: 48px;
  background-size: 48px 48px;
}
</style>
